import { type Node, type Edge } from "@xyflow/react";
import { VariableCategory } from "./DynamicVariableInput";
import { User, FileText, Bot, MessageSquare } from "lucide-react";
import React from "react";

// Variable patterns for detection
const VARIABLE_PATTERNS = {
    doubleBracket: /\{\{([^}]+)\}\}/g,
    singleBracket: /\{(payload\.[^}]+)\}/g,
};

// Interface for workflow context
export interface WorkflowContext {
    hasTrigger: boolean;
    triggerType?: "schedule" | "event";
    eventTriggerType?: string;
    hasFormContext: boolean;
    hasWhatsAppContext: boolean;
    hasPreviousAINode: boolean;
    hasPreviousHumanNode: boolean;
    formId: number | null;
    formFields: { id: string; label: string }[];
}

// All possible valid variables in the system
export const ALL_VALID_VARIABLES: Record<string, string[]> = {
    user: [
        "{{user.email}}",
        "{{user.phone}}",
    ],
    payload: [
        "{payload.body}",
        "{payload.from}",
    ],
    ai: [
        "{ai_result.output.content}",
        "{ai_result.output.confidence_score}",
    ],
    human: [
        "{human_result.action}",
        "{human_result.comments}",
    ],
};

/**
 * Analyze the workflow to determine available context for a given node
 */
export function analyzeWorkflowContext(
    currentNode: Node | null,
    nodes: Node[],
    edges: Edge[]
): WorkflowContext {
    const context: WorkflowContext = {
        hasTrigger: false,
        hasFormContext: false,
        hasWhatsAppContext: false,
        hasPreviousAINode: false,
        hasPreviousHumanNode: false,
        formId: null,
        formFields: [],
    };

    // Find trigger node
    const triggerNode = nodes.find((n) => n.type === "trigger");
    if (triggerNode) {
        context.hasTrigger = true;
        const config = triggerNode.data.config as any;
        context.triggerType = config?.trigger_type;
        context.eventTriggerType = config?.event_trigger_type;

        // Check form context from trigger
        if (config?.trigger_type === "event" && config?.event_trigger_type === "form_submission") {
            context.hasFormContext = true;
            context.formId = config?.event_config?.source_id || null;
        }

        // Check WhatsApp context from trigger
        if (config?.trigger_type === "event" && config?.event_trigger_type === "waha_message") {
            context.hasWhatsAppContext = true;
        }
    }

    // If we have a current node, check its predecessors
    if (currentNode) {
        const visited = new Set<string>();
        const stack = [currentNode.id];

        while (stack.length > 0) {
            const currentId = stack.pop()!;
            if (visited.has(currentId)) continue;
            visited.add(currentId);

            const incomingEdges = edges.filter((edge) => edge.target === currentId);

            for (const edge of incomingEdges) {
                const sourceNode = nodes.find((n) => n.id === edge.source);
                if (!sourceNode) continue;

                // Check for AI node
                if (sourceNode.type === "ai") {
                    context.hasPreviousAINode = true;
                }

                // Check for Human node
                if (sourceNode.type === "human") {
                    context.hasPreviousHumanNode = true;
                }

                // Check for form context from read_form_responses action
                if (sourceNode.type === "action") {
                    const actionConfig = sourceNode.data.config as any;
                    if (actionConfig?.action_type === "read_form_responses") {
                        context.hasFormContext = true;
                    }
                }

                stack.push(sourceNode.id);
            }
        }
    }

    return context;
}

/**
 * Build variable categories based on workflow context
 */
export function buildContextAwareCategories(
    context: WorkflowContext,
    formFields: { id: string; label: string }[] = [],
    actionType?: string,
    nodeType?: string
): VariableCategory[] {
    const categories: VariableCategory[] = [];

    // Define base user variables
    let userVariables = [
        { name: "User Email", variable: "{{user.email}}", description: "Email address of the user" },
        { name: "User Phone", variable: "{{user.phone}}", description: "Phone number of the user" },
    ];

    // Filter based on action type
    if (actionType === "email") {
        userVariables = userVariables.filter(v => v.variable !== "{{user.phone}}");
    } else if (actionType === "whatsapp" || actionType === "sms") {
        userVariables = userVariables.filter(v => v.variable !== "{{user.email}}");
    }

    // User variables (show ONLY for form submission trigger, and skip for certain node types)
    if (context.eventTriggerType === 'form_submission' &&
        nodeType !== "condition" && nodeType !== "ai" && nodeType !== "human" &&
        userVariables.length > 0) {
        categories.push({
            id: "user",
            name: "User",
            icon: React.createElement(User, { className: "h-4 w-4 text-blue-600" }),
            variables: userVariables,
        });
    }

    // Form variables - only if form context exists
    if (context.hasFormContext) {
        // Add form fields if available
        if (formFields.length > 0) {
            categories.push({
                id: "form_fields",
                name: "Form Fields",
                icon: React.createElement(FileText, { className: "h-4 w-4 text-purple-600" }),
                variables: formFields.map((field) => ({
                    name: field.label,
                    variable: nodeType === "condition" ? field.id : `{${field.id}}`,
                    description: `Value of ${field.label}`,
                })),
            });
        }
    }

    // Payload variables - only for WhatsApp context
    if (context.hasWhatsAppContext) {
        categories.push({
            id: "payload",
            name: "WhatsApp Payload",
            icon: React.createElement(MessageSquare, { className: "h-4 w-4 text-emerald-600" }),
            variables: [
                { name: "Message Body", variable: "{payload.body}", description: "Message content from WhatsApp" },
                { name: "Sender Number", variable: "{payload.from}", description: "Sender's phone number" },
            ],
        });
    }

    // AI variables - only if previous AI node exists
    if (context.hasPreviousAINode) {
        categories.push({
            id: "ai",
            name: "AI Response",
            icon: React.createElement(Bot, { className: "h-4 w-4 text-violet-600" }),
            variables: [
                { name: "AI Content", variable: "{ai_result.output.content}", description: "AI generated text" },
                { name: "AI Confidence", variable: "{ai_result.output.confidence_score}", description: "Confidence score (0-1)" },
            ],
        });
    }

    // Human variables - only if previous Human node exists
    if (context.hasPreviousHumanNode) {
        categories.push({
            id: "human",
            name: "Human Review",
            icon: React.createElement(User, { className: "h-4 w-4 text-orange-600" }),
            variables: [
                { name: "Decision", variable: "{human_result.action}", description: "Approve/Reject decision" },
                { name: "Comments", variable: "{human_result.comments}", description: "Reviewer comments" },
            ],
        });
    }

    return categories;
}

/**
 * Get all valid variable strings for the current context
 */
export function getValidVariablesForContext(
    context: WorkflowContext,
    formFields: { id: string; label: string }[] = [],
    nodeType?: string
): string[] {
    const validVars: string[] = [];

    // User variables are only valid for form submission trigger
    if (context.eventTriggerType === 'form_submission') {
        validVars.push(...ALL_VALID_VARIABLES.user);
    }

    if (context.hasFormContext) {
        // Add form field variables
        formFields.forEach((field) => {
            validVars.push(`{${field.id}}`);
            if (nodeType === "condition") {
                validVars.push(field.id);
            }
        });
    }

    if (context.hasWhatsAppContext) {
        validVars.push(...ALL_VALID_VARIABLES.payload);
    }

    if (context.hasPreviousAINode) {
        validVars.push(...ALL_VALID_VARIABLES.ai);
    }

    if (context.hasPreviousHumanNode) {
        validVars.push(...ALL_VALID_VARIABLES.human);
    }

    return validVars;
}

/**
 * Extract all variables from a text string
 */
export function extractVariablesFromText(text: string): string[] {
    const variables: string[] = [];

    // Match {{variable}} pattern
    let match;
    const doublePattern = new RegExp(VARIABLE_PATTERNS.doubleBracket);
    while ((match = doublePattern.exec(text)) !== null) {
        variables.push(match[0]);
    }

    // Match {payload.xxx} pattern
    const singlePattern = new RegExp(VARIABLE_PATTERNS.singleBracket);
    while ((match = singlePattern.exec(text)) !== null) {
        variables.push(match[0]);
    }

    return [...new Set(variables)];
}

/**
 * Validation result interface
 */
export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
}

export interface ValidationError {
    nodeId: string;
    nodeLabel: string;
    field: string;
    invalidVariables: string[];
    message: string;
}

/**
 * Validate all dynamic variables in the workflow
 */
export function validateWorkflowVariables(
    nodes: Node[],
    edges: Edge[],
    formFields: { id: string; label: string }[] = []
): ValidationResult {
    const errors: ValidationError[] = [];

    for (const node of nodes) {
        const config = node.data.config as any;
        const context = analyzeWorkflowContext(node, nodes, edges);
        const validVariables = getValidVariablesForContext(context, formFields, node.type);

        // Get fields to validate based on node type
        const fieldsToValidate = getFieldsToValidate(node, config);

        for (const { fieldName, value } of fieldsToValidate) {
            if (!value || typeof value !== "string") continue;

            const usedVariables = extractVariablesFromText(value);
            const invalidVars = usedVariables.filter((v) => !validVariables.includes(v));

            if (invalidVars.length > 0) {
                errors.push({
                    nodeId: node.id,
                    nodeLabel: (node.data.label as string) || node.id,
                    field: fieldName,
                    invalidVariables: invalidVars,
                    message: `Invalid variable(s) in ${fieldName}: ${invalidVars.join(", ")}`,
                });
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}

/**
 * Get fields that need validation for a node
 */
function getFieldsToValidate(node: Node, config: any): { fieldName: string; value: string }[] {
    const fields: { fieldName: string; value: string }[] = [];

    if (node.type === "action") {
        const actionConfig = config?.action_config || {};

        // Email fields
        if (config?.action_type === "email") {
            if (actionConfig.to) fields.push({ fieldName: "To (Email)", value: actionConfig.to });
            if (actionConfig.email_subject) fields.push({ fieldName: "Email Subject", value: actionConfig.email_subject });
            if (actionConfig.email_body) fields.push({ fieldName: "Email Body", value: actionConfig.email_body });
        }

        // WhatsApp/SMS fields
        if (config?.action_type === "whatsapp" || config?.action_type === "sms") {
            if (actionConfig.number) fields.push({ fieldName: "Phone Number", value: actionConfig.number });
            if (actionConfig.message) fields.push({ fieldName: "Message", value: actionConfig.message });
        }

        // Webhook fields
        if (config?.action_type === "webhook") {
            if (actionConfig.url) fields.push({ fieldName: "Webhook URL", value: actionConfig.url });
            if (actionConfig.body) fields.push({ fieldName: "Request Body", value: actionConfig.body });
        }

        // Ticket/Task fields
        if (config?.action_type === "ticket_creation" || config?.action_type === "task_creation") {
            if (actionConfig.title) fields.push({ fieldName: "Title", value: actionConfig.title });
        }
    }

    // AI node fields
    if (node.type === "ai") {
        const aiConfig = config?.ai_config || {};
        if (aiConfig.prompt) fields.push({ fieldName: "Prompt", value: aiConfig.prompt });
        if (aiConfig.system_prompt) fields.push({ fieldName: "System Prompt", value: aiConfig.system_prompt });
    }

    // Human node fields
    if (node.type === "human") {
        const humanConfig = config?.human_config || {};
        if (humanConfig.instructions) fields.push({ fieldName: "Instructions", value: humanConfig.instructions });
    }

    // Condition node fields - validate rule fields
    if (node.type === "condition") {
        // Check if it's a complex condition with multiple rules
        if (config?.conditions && Array.isArray(config.conditions)) {
            config.conditions.forEach((rule: any, index: number) => {
                if (rule.field) {
                    fields.push({ fieldName: `Condition Rule ${index + 1} Field`, value: rule.field });
                }
            });
        }
        // Check for simple condition (single rule at root level)
        if (config?.field) {
            fields.push({ fieldName: "Condition Field", value: config.field });
        }
    }

    return fields;
}
